import { useEffect, useMemo, useState } from "react";
import { PageHeader } from "@/components/primitives";
import { api, createPlant } from "@/lib/api";
import { toast } from "sonner";
import { Search, Plus, Sprout } from "lucide-react";
import { Link } from "react-router-dom";

export default function Database() {
  const [blooms, setBlooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/community/blooms")
      .then((r) => setBlooms(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const identified = useMemo(
    () => blooms.filter((b) => b.released && b.kind !== "question"),
    [blooms]
  );

  const filtered = useMemo(() => {
    if (!q.trim()) return identified;
    const s = q.toLowerCase();
    return identified.filter((b) =>
      [b.variety_name, b.note, b.user_name, b.user_garden]
        .filter(Boolean).some((x) => x.toLowerCase().includes(s))
    );
  }, [identified, q]);

  const addToMyGarden = async (b) => {
    try {
      await createPlant({
        name: b.variety_name,
        category: "dahlia",
        type: "tuber",
        notes: b.note || `From the community database — by ${b.user_name}${b.user_garden ? `, ${b.user_garden}` : ""}`,
        photos: b.image_data_url ? [b.image_data_url] : [],
      });
      toast.success(`${b.variety_name} added to your garden`);
    } catch {
      toast.error("Could not add to garden");
    }
  };

  return (
    <div data-testid="database-page">
      <PageHeader
        kicker="Confirmed Varieties"
        title="The Database"
        subtitle="A heritage register of every confirmed dahlia"
      />

      <div className="relative mb-4">
        <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-ink-soft" />
        <input
          className="field pl-10"
          placeholder="Search by variety, gardener, garden..."
          value={q}
          onChange={(e) => setQ(e.target.value)}
          data-testid="database-search"
        />
      </div>

      <p className="text-ink-soft mb-4">
        <span data-testid="database-count">{identified.length}</span> confirmed {identified.length === 1 ? "variety" : "varieties"}
        {q && ` · ${filtered.length} matching`}
      </p>

      {loading ? (
        <p className="text-center font-display italic text-ink-soft py-12">Opening the register...</p>
      ) : filtered.length === 0 ? (
        <p className="text-center font-display italic text-ink-soft py-12" data-testid="database-empty">
          {q ? "No varieties match that search." : "No confirmed varieties yet — once the keeper releases identifications, they appear here."}
        </p>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4" data-testid="database-grid">
          {filtered.map((b) => (
            <article key={b.id} className="h-card p-3 flex flex-col items-center text-center" data-testid={`db-card-${b.id}`}>
              <img src={b.image_data_url} alt={b.variety_name}
                   className="w-24 h-24 object-cover rounded-full bg-cream-deep mb-2"
                   draggable={false} />
              <div className="font-display text-lg text-sage-dark leading-tight">{b.variety_name}</div>
              <div className="kicker text-[10px] mt-1">{b.bloom_diameter_cm}cm</div>
              <div className="font-display italic text-xs text-ink-soft mt-1 truncate w-full">
                by {b.user_name}{b.user_garden ? `, ${b.user_garden}` : ""}
              </div>
              {b.note && (
                <p className="text-xs text-ink-soft mt-1 line-clamp-2 italic">{b.note}</p>
              )}
              <button
                onClick={() => addToMyGarden(b)}
                className="btn-heritage text-xs py-1.5 px-3 mt-3 w-full"
                data-testid={`db-add-${b.id}`}
              >
                <Plus size={12} /> Add to my garden
              </button>
            </article>
          ))}
        </div>
      )}

      <p className="text-center font-display italic text-ink-soft mt-10">
        Each entry, confirmed by the keeper. Lifted in autumn, posted across Aotearoa from June.{" "}
        <Link to="/community" className="underline text-sage-dark">Add your own</Link>.
      </p>
    </div>
  );
}
